/**
 * This file contains BigBingo functions that should only be called from
 * within the BigBingo/Learning Time infrastructure. The functions have been
 * moved here from bigbingo.js so that we can lint for files that are importing
 * these functions directly instead of going through the latest BigBingo
 * tooling (which supports Learning Time correctly). Importers outside of the
 * BigBingo/LTv2 infrastructure are legacy should eventually be updated
 * to either use a ConversionContext to access the markConversion function
 * or call markMinorConversions (found in bigbingo.js) for conversions that
 * do not represent a change in Learning Time data).
 *
 * All importers of this file require an eslint exception explaining why it is
 * reasonable to import this file (rare) or that it is done for legacy
 * reasons (unfortunately common).
 *
 * Here is a call graph for this file.
 *
 *       markConversion
 *  (should be accessed via
 *  ConversionContext [1]. used
 *     for Learning Time)
 *            |
 *            |
 *            v
 *           markConversionsWithExtras          markMinorConversions
 *         (ok! used for Learning Time,      (ok! used for Learning Time)
 *          external callers are legacy)        (found in bigbingo.js)
 *              |                                      |
 *              |                                      v
 *              |                    store.dispatch(markMinorConversion)
 *              |                              |
 *              v                              |
 * store.dispatch(markMajorConversion) <--------
 *
 * [1] In a few exceptional cases we have used this function directly
 *     while implementing Learning Time. These have been for cases where we
 *     need to mark conversions outside of react and cannot dependency-inject
 *     markConversion from a react component. The three known cases of this
 *     as of Sept. 2020 are video-player-model.js, modal-navigator-store.js,
 *     and video-lite-entry.js).
 *
 * For more documentation see: http://khanacademy.org/r/conversioncontext.
 */

import type {AnalyticsEvent} from "@khanacademy/event-schemas";

import {actions, store} from "./store.ts";
import type {
    Conversion,
    ConversionName,
    MinorConversionData,
    PartialLearningTimeInfo,
} from "./types.ts";

/**
 * Transform a conversion ID and extras into a timestamped event for dispatching
 * to the store.
 */
export function formatConversions<C extends MinorConversionData | Conversion>(
    data: ReadonlyArray<C>,
    timestampOverride?: number,
): ReadonlyArray<{
    // NOTE(slim): The following type ensures that this function preserves the
    // type of the input conversion ID, since a MajorConversion ID can be any
    // string, but a MinorConversionId must be explicitly allowlisted.
    //
    // If we were to change this to `string` and call this function with an
    // array of MinorConversions, TypeScript would not be able to guarantee that the
    // resulting conversion IDs are also valid MinorConversionIds.
    conversion: C["id"];
    timestamp: number;
    extraJson?: string;
    event?: AnalyticsEvent;
}> {
    const timestamp = timestampOverride ?? Date.now();

    return data.map(({id, extra, event}) => {
        return {
            conversion: id,
            timestamp,
            event,
            ...(extra && {extraJson: JSON.stringify(extra)}),
        };
    });
}

/**
 * Mark an array of conversions with associated metadata as having happened for
 * the current user. External callers of this function are legacy.
 *
 * Returns a Promise which resolves when the request finishes. The promise will
 * not reject, including on network failures.
 */
export const markConversionsWithExtras = (
    conversions: ReadonlyArray<Conversion>,
    // Any Learning Time annotations used for crediting the conversions.
    learningTimeInfo?: PartialLearningTimeInfo,
): Promise<Response | void> => {
    const formatted = formatConversions(conversions);
    return store.dispatch(
        actions.markMajorConversions(formatted, learningTimeInfo),
    );
};

/**
 * Mark a single conversion as having happened for the current user. This
 * function should be accessed via ConversionContext.
 *
 * Returns a Promise which resolves when the request finishes. The promise will
 * not reject, including on network failures.
 */
export const markConversion = (
    // Conversion ID listed in _SOURCE_CONVERSIONS in bigbingo/config.py.
    conversion: ConversionName,
    // Additional details that will be json-encoded and sent to BigQuery.
    conversionExtra?: Record<any, any>,
    // Any Learning Time annotations used for crediting the conversions.
    learningTimeInfo?: PartialLearningTimeInfo,
): Promise<Response | void> => {
    const struct = {
        id: conversion,
        ...(conversionExtra ? {extra: conversionExtra} : null),
    } as const;
    return markConversionsWithExtras([struct], learningTimeInfo);
};
